import { useState } from "react";
import { useGroup } from "../../../../hooks/useGroup";
import { useRiskQuestions } from "../../../../hooks/useRiskQuestions";
import {
  Page,
  Left,
  Right,
  HeaderRow,
  RoleListContainer,
  RoleItem,
  SearchInput,
} from "../styles/component.style";
import { BtnGeneral } from "../../../../styles/Buttons";
import SearchableSelect from "../../../../components/Common/SearchableSelect";

/* ─── Acordeón (estilo idéntico a Users.js / Codificacion.js) ─── */
function Accordion({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: "6px", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 10px",
          background: open ? "#f1f5f9" : "#f8fafc",
          border: "none",
          borderRadius: open ? "6px 6px 0 0" : "6px",
          cursor: "pointer",
          fontSize: "11px",
          fontWeight: "600",
          color: "#1e40af",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {title}
          {count != null && (
            <span
              style={{
                background: "#dbeafe",
                color: "#2563eb",
                borderRadius: "10px",
                padding: "0 6px",
                fontSize: "10px",
                fontWeight: "700",
              }}
            >
              {count}
            </span>
          )}
        </span>
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "6px 8px", borderTop: "1px solid #e2e8f0" }}>{children}</div>}
    </div>
  );
}

/* ─── Field: label arriba + input abajo ─── */
function Field({ lbl, required, children, fullWidth = false }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        gridColumn: fullWidth ? "1 / -1" : "auto",
      }}
    >
      <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
        {lbl}
        {required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export default function RiskQuestions() {
  const { groups } = useGroup();
  const groupsActive = groups.filter(
    (r) => String(r.estado) === "1" || r.estado === 1 || r.estado === true
  );

  const {
    saveQuestions,
    deleteQuestion,
    handleNew,
    setForm,
    form,
    search,
    setSearch,
    loadSubGroup,
    groupSelected,
    groupFilterSelected,
    groupSiSelected,
    groupNoSelected,
    subgroups,
    subgroupsSi,
    subgroupsNo,
    subgroupsFilter,
    loadPreguntaRiesgo,
    preguntaRiesgo,
    selectedPreguntaRiesgo,
    handleSelectPreguntaRiesgo,
    loading,
    saving,
    savingHelp,
    helps,
    newHelp,
    setNewHelp,
    handleAddHelp,
    handleDeleteHelp,
  } = useRiskQuestions();

  const filteredPreguntas = preguntaRiesgo.filter((pr) => {
    const q = search.toLowerCase();
    const preguntaMatch = (pr.Pregunta?.toLowerCase() || "").includes(q);
    const idMatch = String(pr.id || "").includes(q);
    const ordenMatch = String(pr.Orden || "").includes(q);
    return preguntaMatch || idMatch || ordenMatch;
  });

  return (
    <Page>
      {/* ══ PANEL IZQUIERDO ══ */}
      <Left>
        <HeaderRow>
          <h3 style={{ margin: 0, fontSize: "13px" }}>Preguntas Riesgos</h3>
          <BtnGeneral
            bg="cyan"
            padding="4px 8px"
            radius="5px"
            fw="600"
            fs="11px"
            onClick={handleNew}
          >
            Nuevo
          </BtnGeneral>
        </HeaderRow>

        {/* Filtros de grupo y subgrupo */}
        <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
          <SearchableSelect
            key={`filter-group-${groupFilterSelected || "empty"}`}
            value={groupFilterSelected ? String(groupFilterSelected) : ""}
            onValueChange={(val) => {
              loadSubGroup(val, "grupoFiltro");
              if (!val) loadPreguntaRiesgo("");
            }}
            options={groupsActive}
            getOptionValue={(g) => String(g.id)}
            getOptionLabel={(g) => `${g.codigo} - ${g.descripcion}`}
            placeholder="Filtrar por Grupo..."
            multiple={false}
            searchable={true}
            sx={selectSx}
          />

          <SearchableSelect
            key={`filter-subgroup-${groupFilterSelected || "empty"}`}
            value=""
            disabled={!groupFilterSelected}
            onValueChange={(val) => loadPreguntaRiesgo(val)}
            options={subgroupsFilter}
            getOptionValue={(sg) => String(sg.id)}
            getOptionLabel={(sg) => `${sg.codigo} - ${sg.descripcion}`}
            placeholder="Filtrar por Subgrupo..."
            multiple={false}
            searchable={true}
            sx={selectSx}
          />
        </div>

        <SearchInput
          placeholder="Buscar pregunta..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <RoleListContainer>
          {loading ? (
            <div style={{ padding: "12px", color: "#94a3b8", textAlign: "center", fontSize: "11px" }}>
              Cargando preguntas...
            </div>
          ) : filteredPreguntas.length > 0 ? (
            filteredPreguntas.map((pg) => (
              <RoleItem
                key={pg.id}
                active={selectedPreguntaRiesgo === pg.id}
                onClick={() => handleSelectPreguntaRiesgo(pg.id)}
              >
                <div>
                  <strong style={{ fontSize: "12px", lineHeight: "1.3" }}>
                    {pg.Pregunta}
                  </strong>
                  <div style={{ fontSize: "10px", color: "#64748b", marginTop: "3px" }}>
                    Orden: <strong>{pg.Orden ?? "-"}</strong> | Sexo: <strong>{pg.sexo || "A"}</strong>
                    {pg.Accion ? ` | Acción: ${pg.Accion}` : ""}
                  </div>
                </div>
              </RoleItem>
            ))
          ) : (
            <div style={{ padding: "12px", color: "#94a3b8", textAlign: "center", fontSize: "11px" }}>
              {search ? "No se encontraron preguntas" : "No hay preguntas registradas"}
            </div>
          )}
        </RoleListContainer>
      </Left>

      {/* ══ PANEL DERECHO ══ */}
      <Right style={{ overflowY: "auto", padding: "8px 10px" }}>
        <HeaderRow style={{ marginBottom: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: "600", color: "#334155" }}>
            {form.id
              ? `Editor: Pregunta #${form.id}`
              : "Nueva Pregunta de Riesgo"}
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {form.id && (
              <BtnGeneral
                bg="red"
                padding="4px 12px"
                radius="5px"
                fw="600"
                fs="11px"
                onClick={deleteQuestion}
              >
                Eliminar
              </BtnGeneral>
            )}
            <BtnGeneral
              bg="green"
              padding="4px 16px"
              radius="5px"
              fw="600"
              fs="11px"
              disabled={saving}
              onClick={saveQuestions}
            >
              {saving ? "Guardando..." : "Guardar"}
            </BtnGeneral>
          </div>
        </HeaderRow>

        {/* ── Sección: Datos Generales de la Pregunta ── */}
        <Accordion title="Datos de la Pregunta" defaultOpen={true}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
            <Field lbl="ID:">
              <input
                style={{ ...inp, background: "#f8fafc", color: "#94a3b8" }}
                readOnly
                value={form.id || ""}
                placeholder="(Asignado automáticamente)"
              />
            </Field>

            <Field lbl="Orden:" required>
              <input
                type="number"
                style={inp}
                value={form.Orden}
                placeholder="Ej: 10"
                onChange={(e) => setForm({ ...form, Orden: e.target.value })}
              />
            </Field>

            <Field lbl="Texto de la Pregunta:" required fullWidth>
              <textarea
                rows={2}
                style={{ ...inp, height: "auto", resize: "vertical" }}
                value={form.Pregunta}
                placeholder="Escriba la pregunta de riesgo..."
                onChange={(e) => setForm({ ...form, Pregunta: e.target.value })}
              />
            </Field>

            <Field lbl="Sexo Aplicable:" required>
              <SearchableSelect
                key={`select-sexo-${form.id || "new"}`}
                value={form.sexo || "A"}
                onValueChange={(val) => setForm({ ...form, sexo: val })}
                options={[
                  { value: "A", label: "A - Ambos (Masculino y Femenino)" },
                  { value: "M", label: "M - Masculino" },
                  { value: "F", label: "F - Femenino" },
                ]}
                placeholder="Seleccione sexo..."
                multiple={false}
                searchable={false}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Diagnóstico (Dx):">
              <input
                style={inp}
                value={form.Dx}
                placeholder="Ej: Z000, CIE10..."
                onChange={(e) => setForm({ ...form, Dx: e.target.value })}
              />
            </Field>

            <Field lbl="Acción:">
              <input
                style={inp}
                value={form.Accion}
                placeholder="Ej: Activar"
                onChange={(e) => setForm({ ...form, Accion: e.target.value })}
              />
            </Field>

            <Field lbl="Soporte:">
              <input
                style={inp}
                value={form.Soporte}
                placeholder="Soporte de la pregunta..."
                onChange={(e) => setForm({ ...form, Soporte: e.target.value })}
              />
            </Field>

            <Field lbl="Más Información (Descripción ampliada):" fullWidth>
              <input
                style={inp}
                value={form.MasInformacion}
                placeholder="Detalle o información complementaria..."
                onChange={(e) => setForm({ ...form, MasInformacion: e.target.value })}
              />
            </Field>
          </div>
        </Accordion>

        {/* ── Sección: Relación con Grupos y Subgrupos ── */}
        <Accordion title="Relación con Grupos y Subgrupos" defaultOpen={true}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
            <Field lbl="Grupo Principal:">
              <SearchableSelect
                key={`rg-grupo-${groupSelected || "empty"}`}
                value={groupSelected ? String(groupSelected) : ""}
                onValueChange={(val) => loadSubGroup(val, "grupo")}
                options={groupsActive}
                getOptionValue={(g) => String(g.id)}
                getOptionLabel={(g) => `${g.codigo} - ${g.descripcion}`}
                placeholder="Seleccione grupo..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Subgrupo Principal:" required>
              <SearchableSelect
                key={`rg-subgrupo-${form.IdSubgrupos || "empty"}`}
                value={form.IdSubgrupos ? String(form.IdSubgrupos) : ""}
                disabled={!groupSelected}
                onValueChange={(val) => setForm({ ...form, IdSubgrupos: val })}
                options={subgroups}
                getOptionValue={(sg) => String(sg.id)}
                getOptionLabel={(sg) => `${sg.codigo} - ${sg.descripcion}`}
                placeholder="Seleccione subgrupo..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Grupo Condición - SI:">
              <SearchableSelect
                key={`rg-gruposi-${groupSiSelected || "empty"}`}
                value={groupSiSelected ? String(groupSiSelected) : ""}
                onValueChange={(val) => loadSubGroup(val, "grupoSi")}
                options={groupsActive}
                getOptionValue={(g) => String(g.id)}
                getOptionLabel={(g) => `${g.codigo} - ${g.descripcion}`}
                placeholder="Seleccione grupo SI..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Subgrupo Condición - SI:">
              <SearchableSelect
                key={`rg-subgruposi-${form.idSubgrupo_Si || "empty"}`}
                value={form.idSubgrupo_Si ? String(form.idSubgrupo_Si) : ""}
                disabled={!groupSiSelected}
                onValueChange={(val) => setForm({ ...form, idSubgrupo_Si: val })}
                options={subgroupsSi}
                getOptionValue={(sg) => String(sg.id)}
                getOptionLabel={(sg) => `${sg.codigo} - ${sg.descripcion}`}
                placeholder="Seleccione subgrupo SI..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Grupo Condición - NO:">
              <SearchableSelect
                key={`rg-grupono-${groupNoSelected || "empty"}`}
                value={groupNoSelected ? String(groupNoSelected) : ""}
                onValueChange={(val) => loadSubGroup(val, "grupoNo")}
                options={groupsActive}
                getOptionValue={(g) => String(g.id)}
                getOptionLabel={(g) => `${g.codigo} - ${g.descripcion}`}
                placeholder="Seleccione grupo NO..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>

            <Field lbl="Subgrupo Condición - NO:">
              <SearchableSelect
                key={`rg-subgrupono-${form.idSubgrupo_No || "empty"}`}
                value={form.idSubgrupo_No ? String(form.idSubgrupo_No) : ""}
                disabled={!groupNoSelected}
                onValueChange={(val) => setForm({ ...form, idSubgrupo_No: val })}
                options={subgroupsNo}
                getOptionValue={(sg) => String(sg.id)}
                getOptionLabel={(sg) => `${sg.codigo} - ${sg.descripcion}`}
                placeholder="Seleccione subgrupo NO..."
                multiple={false}
                searchable={true}
                sx={selectSx}
              />
            </Field>
          </div>
        </Accordion>

        {/* ── Sección: Ayudas de la Pregunta (AyudaPregunta) ── */}
        {form.id ? (
          <Accordion
            title="Ayudas de la Pregunta (AyudaPregunta)"
            count={helps.length}
            defaultOpen={true}
          >
            {/* Fila para agregar nueva ayuda */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 80px auto",
                gap: "8px",
                alignItems: "flex-end",
                marginBottom: "10px",
                padding: "8px 10px",
                background: "#f8fafc",
                borderRadius: "5px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Field lbl="Texto de Ayuda (MasInfo):" required>
                <input
                  style={inp}
                  value={newHelp.MasInfo}
                  placeholder="Texto detallado de ayuda o guía..."
                  onChange={(e) => setNewHelp({ ...newHelp, MasInfo: e.target.value })}
                />
              </Field>

              <Field lbl="Orden:">
                <input
                  type="number"
                  style={inp}
                  value={newHelp.orden}
                  placeholder="1"
                  onChange={(e) => setNewHelp({ ...newHelp, orden: e.target.value })}
                />
              </Field>

              <div>
                <BtnGeneral
                  bg="teal"
                  padding="4px 12px"
                  radius="4px"
                  fs="11px"
                  fw="600"
                  disabled={savingHelp}
                  onClick={handleAddHelp}
                >
                  {savingHelp ? "Agregando..." : "+ Agregar Ayuda"}
                </BtnGeneral>
              </div>
            </div>

            {/* Tabla de ayudas */}
            {helps.length === 0 ? (
              <p
                style={{
                  color: "#94a3b8",
                  fontSize: "11px",
                  textAlign: "center",
                  padding: "14px 0",
                  margin: 0,
                }}
              >
                Sin ayudas configuradas para esta pregunta. Use el formulario superior para agregar.
              </p>
            ) : (
              <table style={tbl}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ ...th, width: "60px", textAlign: "center" }}>Orden</th>
                    <th style={th}>Texto de Ayuda (MasInfo)</th>
                    <th style={{ ...th, textAlign: "center", width: "52px" }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {helps.map((h) => {
                    const hId = h.Id ?? h.id;
                    return (
                      <tr key={hId} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={{ ...td, textAlign: "center" }}>
                          <span
                            style={{
                              background: "#e0f2fe",
                              color: "#0369a1",
                              fontWeight: "700",
                              fontSize: "11px",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              display: "inline-block",
                            }}
                          >
                            {h.orden ?? "-"}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{ fontSize: "11px", color: "#1e293b", lineHeight: "1.4" }}>
                            {h.MasInfo}
                          </span>
                        </td>
                        <td style={{ ...td, textAlign: "center" }}>
                          <BtnGeneral
                            bg="red"
                            padding="2px 6px"
                            radius="4px"
                            fs="10px"
                            onClick={() => handleDeleteHelp(hId)}
                            title="Eliminar ayuda"
                          >
                            ✕
                          </BtnGeneral>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Accordion>
        ) : (
          <div
            style={{
              padding: "16px",
              background: "#f8fafc",
              border: "1px dashed #cbd5e1",
              borderRadius: "6px",
              textAlign: "center",
              color: "#64748b",
              fontSize: "11px",
              marginTop: "8px",
            }}
          >
            Guarde la pregunta para habilitar la parametrización de sus ayudas.
          </div>
        )}
      </Right>
    </Page>
  );
}

/* ─── Estilos idénticos a Users.js / Codificacion.js ─── */
const inp = {
  width: "100%",
  padding: "3px 7px",
  borderRadius: "4px",
  border: "1px solid #cbd5e1",
  fontSize: "11px",
  boxSizing: "border-box",
  outline: "none",
  marginBottom: 0,
};

const tbl = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "11px",
};

const th = {
  padding: "4px 8px",
  fontWeight: "600",
  color: "#64748b",
  textAlign: "left",
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const td = {
  padding: "5px 8px",
  verticalAlign: "middle",
};

const selectSx = {
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    height: "26px",
    minHeight: "26px",
    borderRadius: "4px",
    fontSize: "11px",
    fontFamily: "var(--font-body)",
    backgroundColor: "#ffffff",
    "& fieldset": {
      borderColor: "#cbd5e1",
    },
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3b82f6",
    },
  },
  "& .MuiSelect-select": {
    padding: "2px 8px !important",
    display: "flex",
    alignItems: "center",
    fontSize: "11px",
    fontFamily: "var(--font-body)",
  },
};
